import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Inbox, Mail, Briefcase, Mic2, Users, Download, Gauge, RefreshCw, LogOut } from "lucide-react";
import { getLeadsSummary, getLeads } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TABS = [
  { key: "subscribers", label: "Subscribers", icon: Mail, cols: ["email"] },
  { key: "inquiries", label: "Consulting Inquiries", icon: Briefcase, cols: ["name", "email", "service", "message"] },
  { key: "speaker_inquiries", label: "Speaker Bookings", icon: Mic2, cols: ["org", "contact", "email", "type", "date", "size", "goal"] },
  { key: "memberships", label: "Memberships", icon: Users, cols: ["tier", "email"] },
  { key: "resource_leads", label: "Resource Leads", icon: Download, cols: ["email"] },
  { key: "quiz_results", label: "Quiz Results", icon: Gauge, cols: ["quiz", "score", "label"] },
];

export default function Admin() {
  const [summary, setSummary] = useState({});
  const [active, setActive] = useState("subscribers");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const loadSummary = () => getLeadsSummary().then(setSummary).catch(() => {});
  const loadRows = (key) => {
    setLoading(true);
    getLeads(key).then(setRows).catch(() => setRows([])).finally(() => setLoading(false));
  };

  useEffect(() => { loadSummary(); }, []);
  useEffect(() => { loadRows(active); }, [active]);

  const tab = TABS.find((t) => t.key === active);
  const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  return (
    <div>
      <section className="bg-navy text-cream">
        <div className="max-w-6xl mx-auto px-5 py-12 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
          <div>
            <p className="font-ui tracking-[0.25em] text-teal text-sm font-semibold uppercase flex items-center gap-2">
              <Inbox size={16} /> BOSSY Admin
            </p>
            <h1 className="font-serif-display text-4xl md:text-5xl font-bold mt-3">Inbox &amp; Leads</h1>
            <p className="font-ui text-white/70 mt-3">Everything captured across the site — inquiries, bookings, subscribers, and more.</p>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover border border-white/20" />
            ) : null}
            <div className="hidden sm:block">
              <p className="font-ui text-sm font-medium">{user?.name || "Admin"}</p>
              <p className="font-ui text-xs text-white/55">{user?.email}</p>
            </div>
            <button onClick={handleLogout} className="inline-flex items-center gap-2 border border-white/30 px-4 py-2 rounded-full font-ui text-sm font-medium hover:bg-white/10 transition-colors">
              <LogOut size={15} /> Log out
            </button>
          </div>
        </div>
      </section>

      {/* Summary cards */}
      <section className="max-w-6xl mx-auto px-5 -mt-8">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {TABS.map((t) => {
            const Icon = t.icon;
            const activeCard = active === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setActive(t.key)}
                className={`text-left rounded-xl p-4 border transition-all ${activeCard ? "bg-navy text-cream shadow-lg" : "bg-white hover:shadow-md"}`}
                style={!activeCard ? { borderColor: "var(--cream-dark)" } : { borderColor: "transparent" }}
              >
                <Icon size={18} className={activeCard ? "text-teal" : "text-teal-dark"} />
                <div className={`font-serif-display text-3xl font-bold mt-2 ${activeCard ? "text-cream" : "text-navy"}`}>{summary[t.key] ?? "—"}</div>
                <div className={`font-ui text-xs mt-1 ${activeCard ? "text-white/70" : "text-navy/55"}`}>{t.label}</div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Table */}
      <section className="max-w-6xl mx-auto px-5 py-10">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-serif-display text-2xl font-bold text-navy flex items-center gap-2">
            {tab && <tab.icon size={22} className="text-teal-dark" />} {tab?.label}
          </h2>
          <button onClick={() => { loadSummary(); loadRows(active); }} className="inline-flex items-center gap-2 text-navy font-ui text-sm font-medium hover:text-orange transition-colors">
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        <div className="bg-white rounded-2xl border overflow-hidden" style={{ borderColor: "var(--cream-dark)" }}>
          {loading ? (
            <p className="font-ui text-navy/50 text-center py-16">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="font-ui text-navy/50 text-center py-16">No entries yet. Submit a form on the site and it'll appear here.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-cream-dark/60 font-ui text-xs uppercase tracking-wider text-navy/60">
                    {tab.cols.map((c) => (<th key={c} className="text-left px-5 py-3 font-semibold">{c}</th>))}
                    <th className="text-left px-5 py-3 font-semibold">received</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={row.id || i} className={i % 2 ? "bg-cream/40" : ""} style={{ borderTop: "1px solid var(--cream-dark)" }}>
                      {tab.cols.map((c) => (
                        <td key={c} className="px-5 py-3 font-ui text-sm text-navy/80 max-w-xs truncate">{String(row[c] ?? "—")}</td>
                      ))}
                      <td className="px-5 py-3 font-ui text-xs text-navy/50 whitespace-nowrap">{fmtDate(row.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <p className="font-ui text-xs text-navy/40 mt-4">Demo dashboard — open to anyone for preview. Add authentication before going live.</p>
      </section>
    </div>
  );
}
